import { useTheme } from '../../context/useTheme';
import { useProject } from '../../context/useProject';
import { useSettings } from '../../hooks';
import { AccountMenu } from '../../auth/AccountMenu';
import { HEADER_ACTIONS_SLOT_ID } from '../common/CalculatorPage';
import './Header.css';

interface HeaderProps {
  onMenuToggle: () => void;
  onSettingsToggle: () => void;
  onHistoryToggle?: () => void;
  onProjectToggle?: () => void;
  onShortcutsToggle?: () => void;
  title: string;
}

export function Header({
  onMenuToggle,
  onSettingsToggle,
  onHistoryToggle,
  onProjectToggle,
  onShortcutsToggle,
  title,
}: HeaderProps) {
  const { theme, toggleTheme } = useTheme();
  const { currentProject } = useProject();
  const { settings } = useSettings();
  const isAluminum = settings.conductorMaterial === 'aluminum';
  return (
    <header className="header">
      <div className="header-left">
        <button
          className="header-menu-btn"
          onClick={onMenuToggle}
          aria-label="Toggle menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
        <div className="header-brand">
          <span className="header-logo">BGD</span>
          <h1 className="header-title">{title}</h1>
        </div>
      </div>

      {/* Calculator pages portal their action bar (mode switch, presets, Reset, Export) here. */}
      <div className="header-actions" id={HEADER_ACTIONS_SLOT_ID} />

      <div className="header-right">
        {isAluminum && (
          <div className="header-aluminum-indicator" role="status" aria-label="Aluminum conductors selected">
            <span className="header-aluminum-badge">Al</span>
          </div>
        )}
        <button
          className="header-icon-btn theme-toggle"
          onClick={toggleTheme}
          aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          title={theme === 'light' ? 'Dark Mode' : 'Light Mode'}
        >
          {theme === 'light' ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          )}
        </button>
        {onProjectToggle && (
          <button
            className={`header-icon-btn ${currentProject ? 'has-badge' : ''}`}
            onClick={onProjectToggle}
            aria-label="Project manager"
            title={currentProject ? `Project: ${currentProject.name}` : 'Project Manager'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            {currentProject && <span className="header-badge" />}
          </button>
        )}
        {onHistoryToggle && (
          <button
            className="header-icon-btn"
            onClick={onHistoryToggle}
            aria-label="View history"
            title="Calculation History"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </button>
        )}
        {onShortcutsToggle && (
          <button
            className="header-icon-btn"
            onClick={onShortcutsToggle}
            aria-label="Keyboard shortcuts"
            title="Keyboard Shortcuts"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </button>
        )}
        <button
          className="header-icon-btn"
          onClick={onSettingsToggle}
          aria-label="Open settings"
          title="Settings"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
        <AccountMenu />
      </div>
    </header>
  );
}

export default Header;
